import { useState, useEffect, useCallback } from "react";
import "./custom-styles.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/modal";
import {
  DollarSign,
  AlertCircle,
  TrendingUp,
  CreditCard,
  Loader2,
  ExternalLink,
  Filter,
  Edit,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import TablePagination from "@/components/masters/TablePagination";
import { Card, CardContent } from "@/components/ui/card";
import { Collection } from "../../pages/Collection";
import api from "@/services/api";
import { categoryApi, divisionApi, zoneApi, userApi } from "@/services/api";
import CustomSearchDropdown from "./CustomSearchDropdown";

interface DueCustomer {
  id: string;
  registrationNumber: string;
  fullName: string;
  contactNumber: string;
  category: string;
  zone: string;
  division: string;
  totalDue: number;
}

interface DueCollection {
  collectionRecordId: string;
  entryNumber: string;
  billingDate: string;
  dueDate: string;
  amount: number;
  totalPaid: number;
  amountDue: number;
  status: string;
  collection: {
    id: string;
    name: string;
    entryNumber: string;
    service: {
      name: string;
      code: string;
    };
    category: string;
    zone: string;
    division: string;
    billingCycle: string;
  };
}

interface DueCollectionResponse {
  data: {
    consumer: DueCustomer;
    totalDue: number;
    collections: DueCollection[];
  };
}

interface UserAccount {
  id: string;
  name: string;
  code: string;
  type: string;
  visibility: string;
  openingBalance: number;
  balanceType: string;
  status: boolean;
}

interface UserResponse {
  data: {
    id: string;
    cashAccounts: UserAccount[];
    bankAccounts: UserAccount[];
  };
}

interface Category {
  id: string;
  name: string;
}

interface Division {
  id: string;
  name: string;
}

interface Zone {
  id: string;
  name: string;
}

interface IdName {
  id: string;
  name: string;
}

interface RecordPaymentProps {
  collections: Collection[];
  setCollections: React.Dispatch<React.SetStateAction<Collection[]>>;
  showPaymentForm: boolean;
  setShowPaymentForm: React.Dispatch<React.SetStateAction<boolean>>;
  isEditPaymentMode: boolean;
  setIsEditPaymentMode: React.Dispatch<React.SetStateAction<boolean>>;
  editingPayment: Collection | null;
  setEditingPayment: React.Dispatch<React.SetStateAction<Collection | null>>;
}

const RecordPayment = ({
  collections,
  setCollections,
  showPaymentForm,
  setShowPaymentForm,
  isEditPaymentMode,
  setIsEditPaymentMode,
  editingPayment,
  setEditingPayment,
}: RecordPaymentProps) => {
  const { toast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [dueFilter, setDueFilter] = useState({
    category: "",
    division: "",
    zone: "",
  });
  const [filteredDues, setFilteredDues] = useState<DueCustomer[]>([]);
  const [allDueConsumers, setAllDueConsumers] = useState<DueCustomer[]>([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState("");
  const [currentDuesPage, setCurrentDuesPage] = useState(1);
  const [duesPerPage] = useState(10);
  const [consumerSearchTerm, setConsumerSearchTerm] = useState("");
  const [keyboardSelectedIndex, setKeyboardSelectedIndex] =
    useState<number>(-1);
  const [selectedDueConsumer, setSelectedDueConsumer] =
    useState<DueCustomer | null>(null);
  const [selectedCollections, setSelectedCollections] = useState<{
    [key: string]: number;
  }>({});
  const [paymentAllocation, setPaymentAllocation] = useState<{
    [key: string]: number;
  }>({});
  const [selectedCashAccount, setSelectedCashAccount] = useState<string | null>(
    null
  );
  const [selectedBankAccount, setSelectedBankAccount] = useState<string | null>(
    null
  );
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [bankAmount, setBankAmount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState(1);
  const [dueCollections, setDueCollections] = useState<DueCollection[]>([]);
  const [userAccounts, setUserAccounts] = useState<{
    cashAccounts: UserAccount[];
    bankAccounts: UserAccount[];
  }>({
    cashAccounts: [],
    bankAccounts: [],
  });
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [showConsumerDetails, setShowConsumerDetails] = useState(false); // New state for consumer details visibility

  const [todayStats, setTodayStats] = useState({
    totalAmount: 0,
    paymentCount: 0,
  });
  const [todayStatsLoading, setTodayStatsLoading] = useState(false);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] =
    useState<React.FormEvent | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [showDetails, setShowDetails] = useState(false);

  // Fixed consumer search handler
  const handleConsumerSearch = useCallback((searchTerm: string) => {
    setDropdownSearchTerm(searchTerm);
  }, []);

  // Fetch user details and accounts
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userData = localStorage.getItem("manterflow-user");
        if (userData) {
          const { userId } = JSON.parse(userData);
          const response = await api.user.getUser(userId);
          setUserAccounts({
            cashAccounts: response.data.cashAccounts,
            bankAccounts: response.data.bankAccounts,
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to fetch user accounts",
          variant: "destructive",
        });
      }
    };

    fetchUserDetails();
  }, []);

  // Fetch today's stats on mount
  useEffect(() => {
    fetchTodayStats();
  }, []);

  // Fetch due collections when a consumer is selected
  const fetchDueCollections = async (consumerId: string) => {
    try {
      const response = await api.collection.getDueCollections(consumerId);
      setDueCollections(response.data.collections);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to fetch due collections",
        variant: "destructive",
      });
    }
  };

  // Fetch today's collection statistics
  const fetchTodayStats = async () => {
    setTodayStatsLoading(true);
    try {
      const response = await api.collection.getTodayCollections();
      setTodayStats({
        totalAmount: response.data.totalAmount || 0,
        paymentCount: response.data.paymentCount || 0,
      });
    } catch (error) {
      console.error("Error fetching today's stats:", error);
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch today's collection statistics.",
        variant: "destructive",
      });
    } finally {
      setTodayStatsLoading(false);
    }
  };

  // Fetch due customers for table (paginated)
  useEffect(() => {
    const fetchDueCustomers = async () => {
      try {
        const response = await api.collection.getDueCustomers({
          page: currentDuesPage,
          limit: duesPerPage,
          ...dueFilter,
          query: consumerSearchTerm,
        });
        setFilteredDues(response.data.docs);
        setTotalPages(Math.ceil(response.data.totalDocs / duesPerPage));
        // Reset keyboard selection when results change
        setKeyboardSelectedIndex(-1);
      } catch (error) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to fetch due customers",
          variant: "destructive",
        });
      }
    };

    fetchDueCustomers();
  }, [currentDuesPage, duesPerPage, dueFilter, consumerSearchTerm]);

  // FIXED: Fetch due consumers for dropdown - always fetch when modal is open
  useEffect(() => {
    const fetchAllDueConsumers = async () => {
      try {
        const response = await api.collection.getDueCustomers({
          page: 1,
          limit: 500,
          ...dueFilter,
          query: dropdownSearchTerm,
          customer: dropdownSearchTerm, // Pass "customer" in params for search
        });
        setAllDueConsumers(response.data.docs);
      } catch (error) {
        toast({
          title: "Error",
          description:
            error.message ?? "Failed to fetch due customers for dropdown",
          variant: "destructive",
        });
      }
    };

    // FIXED: Always fetch when modal is open, regardless of selectedDueConsumer
    if (showPaymentForm) {
      fetchAllDueConsumers();
    }
  }, [dueFilter, dropdownSearchTerm, showPaymentForm]); // Removed selectedDueConsumer dependency

  useEffect(() => {
    if (showPaymentForm && !isEditPaymentMode && userAccounts) {
      // Set defaults when opening new payment form
      if (userAccounts.cashAccounts.length > 0 && selectedCashAccount === null) {
        setSelectedCashAccount(userAccounts.cashAccounts[0].id);
      }
      if (userAccounts.bankAccounts.length > 0 && selectedBankAccount === null) {
        setSelectedBankAccount(userAccounts.bankAccounts[0].id);
      }
    }
  }, [showPaymentForm, isEditPaymentMode, userAccounts]);

  const isButtonDisabled = () => {
    const hasSelectedConsumer = Boolean(selectedDueConsumer);
    const hasSelectedCollections = Object.keys(selectedCollections).length > 0;
    const hasPositiveTotal = calculateTotalSelected() > 0;
    const amountsMatch = Math.abs((cashAmount + bankAmount) - calculateTotalSelected()) < 0.01; // Handle floating point precision
    const hasPaymentAccount = Boolean(selectedCashAccount) || Boolean(selectedBankAccount);
    
    return !hasSelectedConsumer || !hasSelectedCollections || !hasPositiveTotal || !amountsMatch || !hasPaymentAccount;
  };

  // Calculate pagination
  const indexOfLastDue = currentDuesPage * duesPerPage;
  const indexOfFirstDue = indexOfLastDue - duesPerPage;
  const currentDues = filteredDues.slice(indexOfFirstDue, indexOfLastDue);

  const handleDuesPageChange = (page: number) => {
    setCurrentDuesPage(page);
    setKeyboardSelectedIndex(-1); // Reset keyboard selection when page changes
  };

  const handleDueFilterChange = (field: string, value: string) => {
    setDueFilter((prev) => ({ ...prev, [field]: value }));
    setKeyboardSelectedIndex(-1); // Reset keyboard selection when filters change
  };

  const handleDueConsumerSelect = async (consumerId: string) => {
    // If the same consumer is selected again, deselect it
    if (selectedDueConsumer?.id === consumerId) {
      setSelectedDueConsumer(null);
      setKeyboardSelectedIndex(-1);
      return;
    }
  
    const consumer = filteredDues.find((c) => c.id === consumerId);
    if (consumer) {
      setSelectedDueConsumer(consumer);
      await fetchDueCollections(consumerId);
      setSelectedCollections({});
      setPaymentAllocation({});
      
      // Force set default accounts immediately
      const defaultCashAccount = userAccounts.cashAccounts.length > 0 ? userAccounts.cashAccounts[0].id : null;
      const defaultBankAccount = userAccounts.bankAccounts.length > 0 ? userAccounts.bankAccounts[0].id : null;
      
      setSelectedCashAccount(defaultCashAccount);
      setSelectedBankAccount(defaultBankAccount);
      
      setCashAmount(0);
      setBankAmount(0);
      setShowPaymentForm(true);
      setShowEditAccount(false);
      setShowConsumerDetails(false);
      setDropdownSearchTerm("");
    }
  };

  const toggleCollectionSelection = (
    collectionId: string,
    dueAmount: number
  ) => {
    setSelectedCollections((prev) => {
      const newSelections = { ...prev };
      if (newSelections[collectionId]) {
        delete newSelections[collectionId];
      } else {
        newSelections[collectionId] = dueAmount;
      }
      return newSelections;
    });
    setPaymentAllocation((prev) => {
      const newAllocation = { ...prev };
      if (newAllocation[collectionId]) {
        delete newAllocation[collectionId];
      } else {
        newAllocation[collectionId] = dueAmount;
      }
      return newAllocation;
    });
  };

  // Select All / Deselect All functionality
  const handleSelectAll = () => {
    const allSelected = dueCollections.every(
      (col) => selectedCollections[col.collectionRecordId]
    );

    if (allSelected) {
      // Deselect all
      setSelectedCollections({});
      setPaymentAllocation({});
    } else {
      // Select all
      const newSelections: { [key: string]: number } = {};
      const newAllocations: { [key: string]: number } = {};

      dueCollections.forEach((col) => {
        newSelections[col.collectionRecordId] = col.amountDue;
        newAllocations[col.collectionRecordId] = col.amountDue;
      });

      setSelectedCollections(newSelections);
      setPaymentAllocation(newAllocations);
    }
  };

  const updatePaymentAllocation = (collectionId: string, amount: number) => {
    setPaymentAllocation((prev) => ({
      ...prev,
      [collectionId]: amount,
    }));
  };

  const calculateTotalSelected = () => {
    return Object.values(paymentAllocation).reduce(
      (sum, amount) => sum + (Number(amount) || 0),
      0
    );
  };

  const handleEditPayment = useCallback(
    (payment: Collection) => {
      setIsEditPaymentMode(true);
      setEditingPayment(payment);
      const foundConsumer = filteredDues.find(
        (c) =>
          c.fullName === payment.consumerName &&
          c.registrationNumber === payment.accountNumber
      );
      if (foundConsumer) {
        setSelectedDueConsumer(foundConsumer);
        const collection = dueCollections.find(
          (c) => c.collection.service.name === payment.serviceType
        );
        if (collection) {
          setSelectedCollections({
            [collection.collectionRecordId]: parseFloat(
              payment.amount.replace("$", "")
            ),
          });
          setPaymentAllocation({
            [collection.collectionRecordId]: parseFloat(
              payment.amount.replace("$", "")
            ),
          });
        }

        // Set default accounts when editing payment
        if (payment.cashAccount) {
          setSelectedCashAccount(payment.cashAccount);
        } else if (userAccounts.cashAccounts.length > 0) {
          setSelectedCashAccount(userAccounts.cashAccounts[0].id);
        }

        if (payment.bankAccount) {
          setSelectedBankAccount(payment.bankAccount);
        } else if (userAccounts.bankAccounts.length > 0) {
          setSelectedBankAccount(userAccounts.bankAccounts[0].id);
        }

        setCashAmount(payment.cashAmount || 0);
        setBankAmount(payment.bankAmount || 0);
        setShowEditAccount(true); // Show edit account section by default
        setShowConsumerDetails(false); // Reset consumer details visibility
      }
      setShowPaymentForm(true);
    },
    [filteredDues, dueCollections, userAccounts]
  );

  // Intercept form submit to show confirmation dialog
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPendingSubmitEvent(e);
    setShowConfirmDialog(true);
  };

  // Actual payment submit logic
  const handlePaymentSubmit = async () => {
    setShowConfirmDialog(false);
    if (!selectedDueConsumer) {
      toast({
        title: "Error",
        description: "Please select a consumer with due payments.",
        variant: "destructive",
      });
      return;
    }
    const totalSelected = calculateTotalSelected();
    const totalAllocated = cashAmount + bankAmount;
    // Validate pay amounts do not exceed amountDue
    for (const col of dueCollections) {
      const payAmount = paymentAllocation[col.collectionRecordId];
      if (payAmount !== undefined && payAmount <= 0) {
        toast({
          title: "Invalid Pay Amount",
          description: `Pay Amount for collection '${col.collection.name}' must be a positive number.`,
          variant: "destructive",
        });
        return;
      }
      if (payAmount !== undefined && payAmount > col.amountDue) {
        toast({
          title: "Invalid Pay Amount",
          description: `Pay Amount for collection '${
            col.collection.name
          }' cannot exceed the due amount (${col.amountDue.toFixed(2)}).`,
          variant: "destructive",
        });
        return;
      }
    }
    if (totalSelected === 0) {
      toast({
        title: "Error",
        description: "Please select at least one collection to pay.",
        variant: "destructive",
      });
      return;
    }
    if (totalSelected !== totalAllocated) {
      toast({
        title: "Error",
        description: `Payment allocation ($${totalAllocated.toFixed(
          2
        )}) doesn't match selected dues ($${totalSelected.toFixed(2)}).`,
        variant: "destructive",
      });
      return;
    }
    if (!selectedCashAccount && !selectedBankAccount) {
      toast({
        title: "Error",
        description: "Please select at least one payment account.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Prepare the payload
      const payload = {
        collections: Object.entries(paymentAllocation).map(
          ([collectionRecordId, payAmount]) => ({
            collectionRecordId,
            payAmount,
          })
        ),
        cashAccount: selectedCashAccount
          ? {
              id: selectedCashAccount,
              amount: cashAmount,
            }
          : undefined,
        bankAccount: selectedBankAccount
          ? {
              id: selectedBankAccount,
              amount: bankAmount,
            }
          : undefined,
      };

      // Remove undefined accounts from payload
      if (!payload.cashAccount) delete payload.cashAccount;
      if (!payload.bankAccount) delete payload.bankAccount;

      // Make the API call
      const response = await api.payment.mark(payload);

      if (response.message === "success") {
        const selectedCollectionsList = dueCollections.filter(
          (col) => selectedCollections[col.collectionRecordId]
        );
        if (isEditPaymentMode && editingPayment) {
          const updatedCollections = collections.map((collection) => {
            if (collection.id === editingPayment.id) {
              const updatedCollection = selectedCollectionsList[0].collection;
              return {
                ...collection,
                amount: `$${
                  paymentAllocation[
                    selectedCollectionsList[0].collectionRecordId
                  ]
                }`,
                paymentMethod: "Collector Account",
                serviceType: updatedCollection.service.name,
                cashAccount: selectedCashAccount,
                bankAccount: selectedBankAccount,
                cashAmount,
                bankAmount,
              };
            }
            return collection;
          });
          setCollections(updatedCollections);
          toast({
            title: "Payment Updated",
            description: "The payment has been updated successfully.",
          });
        } else {
          const newCollections = selectedCollectionsList.map((col) => {
            const receiptNumber = `RCT-${10047 + collections.length + 1}`;
            return {
              id:
                collections.length > 0
                  ? Math.max(...collections.map((c) => c.id)) + 1
                  : 1,
              receiptNumber,
              consumerName: selectedDueConsumer.fullName,
              accountNumber: selectedDueConsumer.registrationNumber,
              amount: `$${paymentAllocation[col.collectionRecordId]}`,
              paymentMethod: "Collector Account",
              paymentDate: new Date().toISOString().split("T")[0],
              collectedBy: "Current User",
              status: "Completed",
              serviceType: col.collection.service.name,
              cashAccount: selectedCashAccount,
              bankAccount: selectedBankAccount,
              cashAmount,
              bankAmount,
            };
          });
          setCollections([...collections, ...newCollections]);
          toast({
            title: "Payment Recorded",
            description: `${newCollections.length} payment(s) recorded successfully.`,
          });
        }

        // Fetch updated due customers after successful payment
        try {
          const response = await api.collection.getDueCustomers({
            page: currentDuesPage,
            limit: duesPerPage,
            ...dueFilter,
            query: consumerSearchTerm,
          });
          setFilteredDues(response.data.docs);
          setTotalPages(Math.ceil(response.data.totalDocs / duesPerPage));
        } catch (error) {
          console.error("Failed to fetch updated due customers:", error);
        }

        // FIXED: Reset all states when closing
        resetPaymentFormStates();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to record payment",
        variant: "destructive",
      });
    }
  };

  // FIXED: Helper function to reset all payment form states
  const resetPaymentFormStates = () => {
    setSelectedDueConsumer(null);
    setSelectedCollections({});
    setSelectedCashAccount(null);
    setSelectedBankAccount(null);
    setCashAmount(0);
    setBankAmount(0);
    setPaymentAllocation({});
    setShowPaymentForm(false);
    setIsEditPaymentMode(false);
    setEditingPayment(null);
    setShowEditAccount(false);
    setShowConsumerDetails(false); // Reset consumer details visibility
    setDropdownSearchTerm(""); // Clear search term
  };

  // Fetch categories, divisions, zones from API or user data if collector
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        // Get user info from localStorage
        const userData = localStorage.getItem("manterflow-user");
        let role = null;
        let id = null;
        if (userData) {
          const parsed = JSON.parse(userData);
          role = parsed.role;
          id = parsed.userId;
          setUserRole(role);
          setUserId(id);
        }
        if (role === "collector" && id) {
          // Fetch user details and use assigned filters
          const userRes = await userApi.getUser(id);
          console.log(userRes, "response");
          setCategories(
            ((userRes.data.categories as IdName[]) || []).map((c) => ({
              id: c.id,
              name: c.name,
            }))
          );
          setDivisions(
            ((userRes.data.divisions as IdName[]) || []).map((d) => ({
              id: d.id,
              name: d.name,
            }))
          );
          setZones(
            ((userRes.data.zones as IdName[]) || []).map((z) => ({
              id: z.id,
              name: z.name,
            }))
          );
          // Don't set default filter values for collectors
        } else {
          const [catRes, divRes, zoneRes] = await Promise.all([
            categoryApi.getCategories({ status: "true" }),
            divisionApi.getDivisions({ status: "true" }),
            zoneApi.getZones({ status: "true" }),
          ]);
          setCategories(catRes.docs || []);
          setDivisions(divRes.docs || []);
          setZones(zoneRes.docs || []);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to fetch filter data",
          variant: "destructive",
        });
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    // Only set defaults if payment form is open and accounts are loaded
    if (showPaymentForm && userAccounts) {
      // Set default cash account if none selected and accounts available
      if (userAccounts.cashAccounts.length > 0 && !selectedCashAccount) {
        setSelectedCashAccount(userAccounts.cashAccounts[0].id);
      }
      // Set default bank account if none selected and accounts available  
      if (userAccounts.bankAccounts.length > 0 && !selectedBankAccount) {
        setSelectedBankAccount(userAccounts.bankAccounts[0].id);
      }
    }
  }, [userAccounts, showPaymentForm])

  // Find selected account objects for confirmation modal
  const selectedCashAccountObj = userAccounts.cashAccounts.find(
    (acc) => acc.id === selectedCashAccount
  );
  const selectedBankAccountObj = userAccounts.bankAccounts.find(
    (acc) => acc.id === selectedBankAccount
  );

  // Add tabIndex=-1 to prevent the div from receiving focus when using keyboard navigation
  return (
    <div
      className="space-y-6 outline-none focus:outline-none bg-gray-50 min-h-screen p-6"
      tabIndex={-1}
    >
      {/* Updated Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-center justify-between w-full bg-white shadow-sm border-0 p-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-xl font-medium text-gray-600">
              Total Collections
            </p>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {todayStatsLoading ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : (
              `₹${todayStats.totalAmount.toFixed(2)}`
            )}
          </p>
        </div>
        <div className="flex items-center justify-between w-full bg-white shadow-sm border-0 p-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-xl font-medium text-gray-600">Transactions</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {todayStatsLoading ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : (
              `₹${todayStats.paymentCount.toFixed(2)}`
            )}
          </p>
        </div>
      </div>

      {/* Updated Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm border-0 p-6">
        <div className="flex justify-end items-center mb-6">
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
            onClick={() => {
              // FIXED: Reset states when opening via button
              resetPaymentFormStates();
              setShowPaymentForm(true);
            }}
          >
            <ExternalLink className="h-4 w-4" />
            Open Consumer Details
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* Consumer Search */}
            <Input
              id="consumerSearch"
              placeholder="Search by name, reg, phone no"
              value={consumerSearchTerm}
              onChange={(e) => {
                setConsumerSearchTerm(e.target.value);
                setKeyboardSelectedIndex(-1);
              }}
              onKeyDown={(e) => {
                if (filteredDues.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setKeyboardSelectedIndex((prev) =>
                      prev < filteredDues.length - 1 ? prev + 1 : prev
                    );
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setKeyboardSelectedIndex((prev) =>
                      prev > 0 ? prev - 1 : 0
                    );
                  } else if (e.key === "Enter" && keyboardSelectedIndex >= 0) {
                    e.preventDefault();
                    const selectedConsumer =
                      filteredDues[keyboardSelectedIndex];
                    if (selectedConsumer) {
                      handleDueConsumerSelect(selectedConsumer.id);
                    }
                  }
                }
              }}
              className="px-3 py-2 border-b border-gray-300 rounded focus:ring-0 focus:border-gray-500"
            />

            {/* Category Filter */}
            <Select
              value={dueFilter.category}
              onValueChange={(value) =>
                handleDueFilterChange("category", value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="border-b border-gray-300 rounded px-3 py-2 focus:ring-0 focus:border-gray-500 border">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Division Filter */}
            <Select
              value={dueFilter.division}
              onValueChange={(value) =>
                handleDueFilterChange("division", value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="border-b border-gray-300 rounded px-3 py-2 focus:ring-0 focus:border-gray-500">
                <SelectValue placeholder="All divisions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All divisions</SelectItem>
                {divisions.map((division) => (
                  <SelectItem key={division.id} value={division.id}>
                    {division.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Zone Filter */}
            <Select
              value={dueFilter.zone}
              onValueChange={(value) =>
                handleDueFilterChange("zone", value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="border-b border-gray-300 rounded px-3 py-2 focus:ring-0 focus:border-gray-500">
                <SelectValue placeholder="All zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All zones</SelectItem>
                {zones.map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reset Button */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setDueFilter({ category: "", division: "", zone: "" });
                  setConsumerSearchTerm("");
                }}
                className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Updated Consumer Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden consumer-table">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Consumer
                </TableHead>
                <TableHead className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Registration No.
                </TableHead>
                <TableHead className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Contact No.
                </TableHead>
                <TableHead className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Total Due
                </TableHead>
                <TableHead className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Category
                </TableHead>
                <TableHead className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Division / Zone
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDues.length > 0 ? (
                filteredDues.map((consumer, index) => (
                  <TableRow
                    key={consumer.id}
                    className={`
                      cursor-pointer transition-colors hover:bg-blue-50 border-b border-gray-100
                      ${
                        selectedDueConsumer?.id === consumer.id
                          ? "bg-blue-50 border-blue-200"
                          : keyboardSelectedIndex === index
                          ? "bg-gray-50"
                          : "bg-white"
                      }
                    `}
                    onClick={() => handleDueConsumerSelect(consumer.id)}
                  >
                    <TableCell className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {consumer.fullName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-gray-700">
                      {consumer.registrationNumber}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-gray-700">
                      {consumer.contactNumber}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <span className="font-semibold text-gray-900">
                        {consumer.totalDue.toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {consumer.category}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-gray-700">
                      <p className="text-xs">{consumer.division} / {consumer.zone}</p>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {filteredDues.length === 0
                      ? consumerSearchTerm ||
                        dueFilter.category !== "" ||
                        dueFilter.division !== "" ||
                        dueFilter.zone !== ""
                        ? "No consumers with due payments match the selected filters"
                        : "No consumers with due payments found"
                      : "No consumers on this page"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {filteredDues.length > 0 && (
          <div className="flex justify-center mt-6">
            <TablePagination
              currentPage={currentDuesPage}
              totalPages={totalPages}
              onPageChange={handleDuesPageChange}
            />
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      {showPaymentForm && (
        <Dialog
          open={showPaymentForm}
          onOpenChange={(open) => {
            if (!open) {
              resetPaymentFormStates();
            }
          }}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden min-h-[70vh]">
            <DialogHeader className="top-0 bg-background z-10 pb-4">
              <DialogTitle>Record Payment</DialogTitle>
              <DialogDescription>
                Process payment for the selected consumer
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={handleFormSubmit}
              className="w-full flex flex-col flex-grow overflow-hidden"
            >
              <div className="space-y-6 py-4 flex-grow overflow-y-auto">
                {selectedDueConsumer ? (
                  <>
                    {/* Consumer Details Section */}
                    <div className="space-y-4">
                      {/* Consumer Name Dropdown */}
                      <div className="relative z-50 sticky top-0 bg-white shadow-md">
                        <CustomSearchDropdown
                          options={allDueConsumers}
                          value={selectedDueConsumer?.id || ""}
                          onSelect={async (value) => {
                            const consumer = allDueConsumers.find(
                              (c) => c.id === value
                            );
                            if (consumer) {
                              setSelectedDueConsumer(consumer);
                              await fetchDueCollections(consumer.id);
                              setSelectedCollections({});
                              setPaymentAllocation({});
                              setSelectedCashAccount(null);
                              setSelectedBankAccount(null);
                              setCashAmount(0);
                              setBankAmount(0);
                              setShowEditAccount(false);
                              setShowConsumerDetails(false); // Reset consumer details visibility
                              setSelectedCashAccount(userAccounts.cashAccounts.length > 0 ? userAccounts.cashAccounts[0].id : null);
                              setSelectedBankAccount(userAccounts.bankAccounts.length > 0 ? userAccounts.bankAccounts[0].id : null);
                            }
                          }}
                          onSearch={handleConsumerSearch}
                          placeholder="Search and select consumer"
                          className="w-full"
                        />
                      </div>

                      {/* Customer Details Toggle Button */}
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setShowConsumerDetails(!showConsumerDetails)}
                        className="flex items-center gap-2 text-left justify-start p-0 h-auto hover:bg-transparent hover:text-black"
                      >
                        {showConsumerDetails ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        <span className="underline">Customer Details</span>
                      </Button>

                      {/* Consumer Details Grid (Conditionally Rendered) */}
                      {showConsumerDetails && (
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 p-4 border rounded-md bg-muted/30">
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Consumer Name
                            </Label>
                            <p className="font-medium text-sm">
                              {selectedDueConsumer.fullName}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Account Number
                            </Label>
                            <p className="font-medium text-sm">
                              {selectedDueConsumer.registrationNumber}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Category
                            </Label>
                            <p className="font-medium text-sm">
                              {selectedDueConsumer.category}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Division
                            </Label>
                            <p className="font-medium text-sm">
                              {selectedDueConsumer.division}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Zone
                            </Label>
                            <p className="font-medium text-sm">
                              {selectedDueConsumer.zone}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Total Selected and Allocated Summary */}
                    {Object.keys(selectedCollections).length > 0 && (
                      <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/30">
                        <div>
                          <Label className="text-sm text-muted-foreground">
                            Total Selected
                          </Label>
                          <p className="text-xl font-bold">
                            ₹{calculateTotalSelected().toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">
                            Total Allocated
                          </Label>
                          <p className="text-xl font-bold">
                            ₹{(cashAmount + bankAmount).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}

                    {cashAmount + bankAmount !== calculateTotalSelected() &&
                      Object.keys(selectedCollections).length > 0 && (
                        <div className="flex items-center p-3 text-sm rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800">
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Payment allocation (₹
                          {(cashAmount + bankAmount).toFixed(2)}) does not match
                          selected dues (₹{calculateTotalSelected().toFixed(2)}
                          ).
                        </div>
                      )}

                    <div className="space-y-4 flex-grow flex flex-col">
                      {/* Collections Section */}
                      <div className="bg-muted/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium flex items-center gap-2">
                            <span>
                              Payment for {selectedDueConsumer.fullName}
                            </span>
                          </h3>
                          <div className="text-right flex items-center gap-2">
                            <Label className="text-sm text-muted-foreground">
                              Total Due:
                            </Label>
                            <p className="font-bold text-destructive">
                              ₹{selectedDueConsumer.totalDue.toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="border rounded-md w-full flex-grow">
                          <div>
                            <Table className="w-[800px] md:w-full">
                              <TableHeader className="sticky top-0 bg-background z-10">
                                <TableRow>
                                  <TableHead>Collection</TableHead>
                                  <TableHead>Service</TableHead>
                                  <TableHead>Due Amount</TableHead>
                                  <TableHead>Due Date</TableHead>
                                  <TableHead>Pay Amount</TableHead>
                                  <TableHead className="w-[80px]">
                                    <div className="flex items-center gap-2">
                                      <span>Select</span>
                                      <Checkbox
                                        checked={
                                          dueCollections.length > 0 &&
                                          dueCollections.every(
                                            (col) =>
                                              selectedCollections[
                                                col.collectionRecordId
                                              ]
                                          )
                                        }
                                        onCheckedChange={handleSelectAll}
                                        className="ml-2"
                                      />
                                    </div>
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {dueCollections.map((col) => (
                                  <TableRow
                                    key={col.collectionRecordId}
                                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() =>
                                      toggleCollectionSelection(
                                        col.collectionRecordId,
                                        col.amountDue
                                      )
                                    }
                                  >
                                    <TableCell className="font-medium">
                                      {col.collection.name}
                                    </TableCell>
                                    <TableCell>
                                      {col.collection.service.name}
                                    </TableCell>
                                    <TableCell>
                                      ₹{col.amountDue.toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                      {new Date(col.dueDate).toLocaleDateString(
                                        "en-GB",
                                        {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                        }
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {selectedCollections[
                                        col.collectionRecordId
                                      ] !== undefined && (
                                        <div className="flex items-center space-x-2">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            max={col.amountDue}
                                            min={0.01}
                                            value={
                                              paymentAllocation[
                                                col.collectionRecordId
                                              ] || ""
                                            }
                                            onChange={(e) => {
                                              let val =
                                                parseFloat(e.target.value) || 0;
                                              if (val < 0) val = 0;
                                              if (val === 0) val = 0;
                                              if (val > col.amountDue)
                                                val = col.amountDue;
                                              updatePaymentAllocation(
                                                col.collectionRecordId,
                                                val
                                              );
                                            }}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                            className="w-24"
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Checkbox
                                        checked={Boolean(
                                          selectedCollections[
                                            col.collectionRecordId
                                          ]
                                        )}
                                        onCheckedChange={() =>
                                          toggleCollectionSelection(
                                            col.collectionRecordId,
                                            col.amountDue
                                          )
                                        }
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>

                      {/* Payment Details Section */}
                      {Object.keys(selectedCollections).length > 0 && (
                        <div className="space-y-4">
                          <Separator />

                          {/* Payment Details Header */}
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold">
                                Payment Details
                              </h3>
                              <p className="text-xl font-bold">
                                ₹{calculateTotalSelected().toFixed(2)}
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-8">
                              <div>
                                <Label className="text-sm">Cash</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={cashAmount || ""}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (isNaN(value) || value < 0) {
                                      setCashAmount(0);
                                    } else {
                                      setCashAmount(value);
                                    }
                                  }}
                                  placeholder="Enter cash amount"
                                  className="text-center"
                                />
                              </div>
                              <div>
                                <Label className="text-sm">Bank</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={bankAmount || ""}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (isNaN(value) || value < 0) {
                                      setBankAmount(0);
                                    } else {
                                      setBankAmount(value);
                                    }
                                  }}
                                  placeholder="Enter bank amount"
                                  className="text-center"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Edit Payment Account Button */}
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setShowEditAccount(!showEditAccount)}
                            className="flex items-center gap-2 text-left justify-start p-0 h-auto hover:bg-transparent hover:text-black"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="underline">
                              Edit Payment Account
                            </span>
                          </Button>

                          {/* Account Selection Section (Collapsible) */}
                          {showEditAccount && (
                            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium">
                                    Cash Account
                                  </Label>
                                  <Select
                                    value={selectedCashAccount || ""}
                                    onValueChange={(value) => {
                                      setSelectedCashAccount(value);
                                      if (!value) setCashAmount(0);
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select Cash Account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {userAccounts.cashAccounts.map(
                                        (account) => (
                                          <SelectItem
                                            key={account.id}
                                            value={account.id}
                                          >
                                            {account.name}
                                          </SelectItem>
                                        )
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium">
                                    Bank Account
                                  </Label>
                                  <Select
                                    value={selectedBankAccount || ""}
                                    onValueChange={(value) => {
                                      setSelectedBankAccount(value);
                                      if (!value) setBankAmount(0);
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select Bank Account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {userAccounts.bankAccounts.map(
                                        (account) => (
                                          <SelectItem
                                            key={account.id}
                                            value={account.id}
                                          >
                                            {account.name}
                                          </SelectItem>
                                        )
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <Label>Consumers *</Label>
                    <div className="relative z-50 sticky top-0 bg-white shadow-md">
                      <CustomSearchDropdown
                        options={allDueConsumers}
                        value={selectedDueConsumer?.id || ""}
                        onSelect={async (value) => {
                          const consumer = allDueConsumers.find(
                            (c) => c.id === value
                          );
                          if (consumer) {
                            setSelectedDueConsumer(consumer);
                            await fetchDueCollections(consumer.id);
                            setSelectedCollections({});
                            setPaymentAllocation({});
                            setSelectedCashAccount(null);
                            setSelectedBankAccount(null);
                            setCashAmount(0);
                            setBankAmount(0);
                            setShowEditAccount(false);
                            setShowConsumerDetails(false); // Reset consumer details visibility
                            setSelectedCashAccount(userAccounts.cashAccounts.length > 0 ? userAccounts.cashAccounts[0].id : null);
                            setSelectedBankAccount(userAccounts.bankAccounts.length > 0 ? userAccounts.bankAccounts[0].id : null);
                          }
                        }}
                        onSearch={handleConsumerSearch}
                        placeholder="Search and select consumer"
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="bottom-0 bg-background z-10 pt-4 border-t">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => resetPaymentFormStates()}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isButtonDisabled()}
                >
                  Record Payment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md p-4">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>
              Ensure all amounts are correct. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1 mb-2">
            <div>
              <b>Consumer:</b> {selectedDueConsumer?.fullName}
            </div>
            <div>
              <b>Total:</b> ₹{calculateTotalSelected().toFixed(2)}
            </div>
            <div>
              <b>Cash:</b> ₹{cashAmount.toFixed(2)}
              {selectedCashAccountObj && (
                <span className="ml-1 text-muted-foreground">
                  ({selectedCashAccountObj.name})
                </span>
              )}
              <b className="ml-4">Bank:</b> ₹{bankAmount.toFixed(2)}
              {selectedBankAccountObj && (
                <span className="ml-1 text-muted-foreground">
                  ({selectedBankAccountObj.name})
                </span>
              )}
            </div>
            <div className="text-xs text-destructive mt-1">
              No changes allowed after this.
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handlePaymentSubmit}>
              Confirm & Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecordPayment;
